import { type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Link } from 'react-router'
import { Tooltip } from './Tooltip'

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'outline-primary' | 'outline-secondary' | 'outline-success' | 'outline-info' | 'outline-danger' | 'ghost'
export type ButtonSize = 'sm' | 'md'

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-primary-500 text-on-dark border border-transparent hover:bg-primary-600',
  // Palette dedicata: i neutri secondary si invertono in dark mode, questi token
  // mantengono invece uno sfondo scuro leggibile con testo chiaro.
  secondary: 'bg-button-secondary text-on-dark border border-transparent hover:bg-button-secondary-hover',
  success: 'bg-success-700 text-on-dark border border-transparent hover:bg-success-800',
  danger: 'bg-danger-600 text-on-dark border border-transparent hover:bg-danger-700',
  'outline-primary': 'bg-transparent text-link border border-primary-500 hover:bg-primary-50',
  'outline-secondary': 'bg-transparent text-body border border-border-strong hover:bg-surface',
  'outline-success': 'bg-transparent text-positive border border-success-500 hover:bg-success-50',
  'outline-info': 'bg-transparent text-informative border border-info-500 hover:bg-info-50',
  'outline-danger': 'bg-transparent text-negative border border-danger-500 hover:bg-danger-50',
  ghost: 'bg-transparent text-body border border-transparent hover:bg-surface-strong',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'text-sm px-2.5 py-1.5 gap-1.5',
  md: 'text-sm px-4 py-2 gap-2',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
  iconOnly?: boolean
}

export function Button({ variant = 'primary', size = 'md', icon, iconOnly, className = '', children, title, ...rest }: ButtonProps) {
  const button = (
    <button
      className={`inline-flex items-center justify-center rounded-md font-medium transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${variantClasses[variant]} ${sizeClasses[size]} ${iconOnly ? 'px-2' : ''} ${className}`}
      aria-label={iconOnly ? title : undefined}
      {...rest}
    >
      {icon}
      {!iconOnly && children}
    </button>
  )
  return title ? <Tooltip content={title}>{button}</Tooltip> : button
}

interface LinkButtonProps {
  to: string
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
  iconOnly?: boolean
  className?: string
  children?: ReactNode
  title?: string
}

export function LinkButton({ to, variant = 'primary', size = 'md', icon, iconOnly, className = '', children, title }: LinkButtonProps) {
  const link = (
    <Link
      to={to}
      aria-label={iconOnly ? title : undefined}
      className={`inline-flex items-center justify-center rounded-md font-medium transition-colors cursor-pointer ${variantClasses[variant]} ${sizeClasses[size]} ${iconOnly ? 'px-2' : ''} ${className}`}
    >
      {icon}
      {!iconOnly && children}
    </Link>
  )
  return title ? <Tooltip content={title}>{link}</Tooltip> : link
}
